import { Suspense, lazy } from 'react';
import { Navigate, useRoutes, useLocation, Outlet } from 'react-router-dom';
// layouts
import DashboardLayout from '../layouts/dashboard';
import UserLayout from '../layouts/dashboard/userLayout';
import LogoOnlyLayout from '../layouts/LogoOnlyLayout';
// guards
import GuestGuard from '../guards/GuestGuard';
import AuthGuard from '../guards/AuthGuard';
import RoleBasedGuard from '../guards/RoleBasedGuard';
// config
// components
import { USER_ROLES } from '../constants/keywords';
import LoadingScreen from '../components/LoadingScreen';
import { PATH_ADMIN_AUTH, PATH_AUTH, PATH_DASHBOARD, PATH_USER_DASHBOARD } from './paths';
import useAuth from '../hooks/useAuth';

// ----------------------------------------------------------------------

const Loadable = (Component) => (props) => {
  const { pathname } = useLocation();

  return (
    <Suspense fallback={<LoadingScreen isDashboard={pathname.includes('/dashboard')} />}>
      <Component {...props} />
    </Suspense>
  );
};

export default function Router() {
  const { user } = useAuth();

  return useRoutes([
    // Admin Routes

    {
      path: '/admin',
      element: <LogoOnlyLayout />,
      children: [
        { element: <Navigate to={PATH_ADMIN_AUTH.login} replace />, index: true },
        {
          path: 'login',
          element: (
            <GuestGuard>
              <Login />
            </GuestGuard>
          ),
        },
        {
          path: 'reset-password',
          element: (
            <GuestGuard>
              <ResetPassword />
            </GuestGuard>
          ),
        },
        {
          path: 'verify',
          element: (
            <GuestGuard>
              <VerifyCode />
            </GuestGuard>
          ),
        },
      ],
    },

    {
      path: 'admin/dashboard',
      element: (
        <AuthGuard>
          <RoleBasedGuard accessibleRoles={[USER_ROLES.ADMIN]}>
            <DashboardLayout />
          </RoleBasedGuard>
        </AuthGuard>
      ),
      children: [
        {
          element: <Navigate to={PATH_DASHBOARD.general.app} replace />,
          index: true,
        },
        {
          path: 'home',
          element: <GeneralDashboard />,
        },

        {
          path: 'user',
          children: [
            {
              element: <Navigate to="/admin/dashboard/user/account" replace />,
              index: true,
            },
            { path: 'account', element: <UserAccount /> },
          ],
        },
        {
          path: 'users',
          element: <Outlet />,
          children: [{ path: 'list', element: <Users /> }],
        },
        {
          path: 'instructions',
          element: <Instructions />,
        },
        {
          path: 'funds',
          element: <Outlet />,
          children: [
            {
              path: ':userId/details',
              element: <Funds />,
            },
          ],
        },
      ],
    },

    // User Routes
    {
      path: '/',
      element: <LogoOnlyLayout />,
      children: [
        { element: <Navigate to={PATH_AUTH.login} replace />, index: true },
        {
          path: 'login',
          element: (
            <GuestGuard>
              <Login />
            </GuestGuard>
          ),
        },
      ],
    },

    {
      path: 'dashboard',
      element: (
        <AuthGuard>
          <RoleBasedGuard accessibleRoles={[USER_ROLES.USER]}>
            <UserLayout />
          </RoleBasedGuard>
        </AuthGuard>
      ),
      children: [
        {
          element: <Navigate to={PATH_USER_DASHBOARD.general.app} replace />,
          index: true,
        },
        {
          path: 'home',
          element: <UserDashboard />,
        },
        {
          path: 'profile',
          element: <Profile />,
        },
        {
          path: 'fund',
          element: <Fund />,
        },
        {
          path: 'trading-summary',
          element: <TradingSummary />,
        },
        {
          path: 'transactions',
          element: <Transactions />,
        },
      ],
    },

    // Main Routes
    {
      path: '*',
      element: <LogoOnlyLayout />,
      children: [
        { path: '404', element: <NotFound /> },
        { path: '*', element: <Navigate to="/404" replace /> },
      ],
    },

    { path: '*', element: <Navigate to="/404" replace /> },
  ]);
}

// AUTHENTICATION
const Login = Loadable(lazy(() => import('../pages/auth/Login')));
const ResetPassword = Loadable(lazy(() => import('../pages/auth/ResetPassword')));
const VerifyCode = Loadable(lazy(() => import('../pages/auth/VerifyCode')));

// DASHBOARD

// User
const UserAccount = Loadable(lazy(() => import('../pages/dashboard/UserAccount')));

// GENERAL
const GeneralDashboard = Loadable(lazy(() => import('../pages/dashboard/GeneralDashboard')));

// Users
const Users = Loadable(lazy(() => import('../pages/dashboard/users')));

// Instructions

const Instructions = Loadable(lazy(() => import('../pages/dashboard/instructions')));

// Funds

const Funds = Loadable(lazy(() => import('../pages/dashboard/funds')));

// MAIN
const NotFound = Loadable(lazy(() => import('../pages/Page404')));

/// user panel

const UserDashboard = Loadable(lazy(() => import('../pages/user/dashboard')));
const Fund = Loadable(lazy(() => import('../pages/user/fund')));
const Profile = Loadable(lazy(() => import('../pages/user/profile')));
const Transactions = Loadable(lazy(() => import('../pages/user/transactions')));
const TradingSummary = Loadable(lazy(() => import('../pages/user/trading-summary')));
